import type { Session, User } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  configured: boolean
  authError: string | null
  access: AccessSnapshot | null
  clearAuthError: () => void
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>
  signUp: (email: string, password: string, captchaToken?: string) => Promise<'check-email' | 'signed-in'>
  signInWithGoogle: () => Promise<void>
  sendPasswordReset: (email: string, captchaToken?: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
  signOut: () => Promise<void>
}

export interface AccessSnapshot {
  mode: 'open' | 'paid_only'
  status: 'active' | 'pending' | 'suspended'
  planCode: string
  accessUntil: string | null
  allowed: boolean
  role: 'user' | 'admin' | 'developer'
}

interface AuthorizedSession {
  session: Session
  access: AccessSnapshot
}

const AuthContext = createContext<AuthState | null>(null)
const authorizationRequests = new Map<string, Promise<AuthorizedSession>>()

const openAccess: AccessSnapshot = {
  mode: 'open',
  status: 'active',
  planCode: 'free',
  accessUntil: null,
  allowed: true,
  role: 'user',
}

function parseAccess(data: unknown): AccessSnapshot {
  if (!data || typeof data !== 'object') return openAccess
  const value = data as Record<string, unknown>
  const mode = value.mode === 'paid_only' ? 'paid_only' : 'open'
  const status = value.status === 'pending' || value.status === 'suspended' ? value.status : 'active'
  return {
    mode,
    status,
    planCode: typeof value.plan_code === 'string' ? value.plan_code : 'free',
    accessUntil: typeof value.access_until === 'string' ? value.access_until : null,
    allowed: typeof value.allowed === 'boolean' ? value.allowed : mode === 'open',
    role: value.role === 'developer' || value.role === 'admin' ? value.role : 'user',
  }
}

function accessMessage(message: string): string {
  if (message.includes('belum diverifikasi')) return 'Verifikasi email kamu dulu lewat tautan di kotak masuk.'
  if (message.includes('owner allowlist')) return 'Pendaftaran publik belum aktif di database. Hubungi admin Dhuwit.'
  return 'Akun belum bisa dibuka. Coba lagi atau hubungi admin Dhuwit.'
}

function authorize(session: Session): Promise<AuthorizedSession> {
  const pending = authorizationRequests.get(session.access_token)
  if (pending) return pending
  const request = (async () => {
    if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
    const { data, error } = await supabase.rpc('bootstrap_owner')
    if (error) throw new Error(accessMessage(error.message))
    return { session, access: parseAccess(data) }
  })()
  authorizationRequests.set(session.access_token, request)
  void request.then(
    () => authorizationRequests.delete(session.access_token),
    () => authorizationRequests.delete(session.access_token),
  )
  return request
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [access, setAccess] = useState<AccessSnapshot | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    if (!supabase) return
    const client = supabase
    let active = true
    let sequence = 0
    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      const current = ++sequence
      if (!nextSession) {
        setSession(null)
        setAccess(null)
        setLoading(false)
        return
      }
      setLoading(true)
      // Supabase auth methods must run outside the synchronous auth callback.
      setTimeout(() => {
        void authorize(nextSession).then(
          (approved) => {
            if (!active || current !== sequence) return
            setSession(approved.session)
            setAccess(approved.access)
            setAuthError(null)
            setLoading(false)
          },
          async (error: unknown) => {
            if (!active || current !== sequence) return
            setAuthError(error instanceof Error ? error.message : 'Akun tidak dapat dibuka.')
            setSession(null)
            setAccess(null)
            await client.auth.signOut()
            if (active) setLoading(false)
          },
        )
      }, 0)
    })
    return () => {
      active = false
      sequence++
      data.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: isSupabaseConfigured,
      authError,
      access,
      clearAuthError: () => setAuthError(null),
      async signIn(email, password, captchaToken) {
        if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
        setAuthError(null)
        const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password, options: { captchaToken } })
        if (error) throw new Error('Email atau kata sandi salah, atau email belum diverifikasi.')
        if (!data.session) throw new Error('Sesi belum tersedia. Coba masuk lagi.')
        try {
          await authorize(data.session)
        } catch (caught) {
          await supabase.auth.signOut()
          throw caught
        }
      },
      async signUp(email, password, captchaToken) {
        if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
        setAuthError(null)
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: `${window.location.origin}/login`, captchaToken },
        })
        if (error) {
          if (error.message.toLowerCase().includes('email address not authorized')) throw new Error('Pendaftaran email belum aktif untuk umum. Admin perlu menyiapkan layanan email.')
          throw error
        }
        if (!data.session) return 'check-email'
        try {
          await authorize(data.session)
          return 'signed-in'
        } catch (caught) {
          await supabase.auth.signOut()
          throw caught
        }
      },
      async signInWithGoogle() {
        if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
        setAuthError(null)
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: `${window.location.origin}/login` },
        })
        if (error) throw new Error('Login Google belum tersedia. Coba lagi atau pakai email.')
      },
      async sendPasswordReset(email, captchaToken) {
        if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
          captchaToken,
        })
        if (error) throw error
      },
      async updatePassword(password) {
        if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
        const { error } = await supabase.auth.updateUser({ password })
        if (error) throw error
      },
      async signOut() {
        if (!supabase) return
        const { error } = await supabase.auth.signOut()
        if (error) throw error
      },
    }),
    [access, authError, loading, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth harus dipakai di dalam AuthProvider')
  return context
}
